import { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, CircularProgress, Alert,
  IconButton, Tooltip, SwipeableDrawer,
  ToggleButton, ToggleButtonGroup,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import TuneIcon from "@mui/icons-material/Tune";
import CloseIcon from "@mui/icons-material/Close";
import WbSunnyIcon from "@mui/icons-material/WbSunny";

// ─── Helpers ──────────────────────────────────────────────────────────────────
function fmtTime(raw) {
  if (!raw) return "—";
  const [h, m] = raw.replace("_", ":").split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${ampm}`;
}

function sunProgress(sunriseRaw, sunsetRaw) {
  if (!sunriseRaw || !sunsetRaw) return 50;
  const now = new Date();
  const nowMins = now.getHours() * 60 + now.getMinutes();
  const [rh, rm] = sunriseRaw.replace("_", ":").split(":").map(Number);
  const [sh, sm] = sunsetRaw.replace("_", ":").split(":").map(Number);
  const rise = rh * 60 + rm;
  const set = sh * 60 + sm;
  if (nowMins <= rise) return 0;
  if (nowMins >= set) return 100;
  return Math.round(((nowMins - rise) / (set - rise)) * 100);
}

function daylightHours(sunriseRaw, sunsetRaw) {
  if (!sunriseRaw || !sunsetRaw) return "";
  const [rh, rm] = sunriseRaw.replace("_", ":").split(":").map(Number);
  const [sh, sm] = sunsetRaw.replace("_", ":").split(":").map(Number);
  const totalMins = (sh * 60 + sm) - (rh * 60 + rm);
  return `${Math.floor(totalMins / 60)}h ${totalMins % 60}m daylight`;
}

const COND_ICON = {
  sunny: "☀️", clear: "☀️", "mostly-sunny": "🌤", "partly-cloudy": "⛅",
  cloudy: "☁️", overcast: "☁️", fog: "🌫️", drizzle: "🌦️",
  rain: "🌧️", snow: "❄️", storm: "⛈️", windy: "💨",
};
function condIcon(cond) { return COND_ICON[cond?.toLowerCase()] || "🌤"; }
function condLabel(cond) {
  if (!cond) return "—";
  return cond.charAt(0).toUpperCase() + cond.slice(1).replace(/-/g, " ");
}

const POLLEN_COLORS = {
  "very_high": { bg: "#FCEBEB", text: "#A32D2D" },
  "high":      { bg: "#FAECE7", text: "#993C1D" },
  "medium":    { bg: "#FAEEDA", text: "#854F0B" },
  "low":       { bg: "#EAF3DE", text: "#3B6D11" },
  "very_low":  { bg: "#EAF3DE", text: "#3B6D11" },
  "none":      { bg: "#f1f5f9", text: "#64748b" },
};
function pollenLabel(val) {
  const m = { very_high:"Very high", high:"High", medium:"Moderate", low:"Low", very_low:"Very low", none:"None" };
  return m[val?.toLowerCase()] || val || "—";
}
function pollenColors(val) { return POLLEN_COLORS[val?.toLowerCase()] || { bg: "#f1f5f9", text: "#64748b" }; }

const AQ_COLORS = {
  good:      { bg: "#EAF3DE", text: "#3B6D11", label: "Good" },
  low:       { bg: "#EAF3DE", text: "#3B6D11", label: "Good" },
  medium:    { bg: "#FAEEDA", text: "#854F0B", label: "Moderate" },
  high:      { bg: "#FAECE7", text: "#993C1D", label: "Poor" },
  very_high: { bg: "#FCEBEB", text: "#A32D2D", label: "Very poor" },
};
function aqColors(val) { return AQ_COLORS[val?.toLowerCase()] || { bg: "#f1f5f9", text: "#64748b", label: val || "—" }; }

function respLabel(val) {
  const n = parseInt(val) || 0;
  if (n <= 2) return "Low";
  if (n <= 5) return "Moderate";
  if (n <= 7) return "High";
  return "Very high";
}
function respColors(val) {
  const n = parseInt(val) || 0;
  if (n <= 2) return { bg: "#EAF3DE", text: "#3B6D11" };
  if (n <= 5) return { bg: "#FAEEDA", text: "#854F0B" };
  return { bg: "#FAECE7", text: "#993C1D" };
}

function levelColors(val) {
  const v = val?.toLowerCase();
  if (v === "low" || v === "very_low" || v === "no")  return { bg: "#EAF3DE", text: "#3B6D11" };
  if (v === "medium" || v === "moderate")              return { bg: "#FAEEDA", text: "#854F0B" };
  if (v === "high" || v === "very_high")               return { bg: "#FAECE7", text: "#993C1D" };
  return { bg: "#f1f5f9", text: "#64748b" };
}
function levelLabel(val) {
  const m = { very_high:"Very high", high:"High", medium:"Moderate", low:"Low", very_low:"Very low", no:"No", yes:"Yes" };
  return m[val?.toLowerCase()] || val || "—";
}

function awoTempLabel(val) {
  if (!val) return "—";
  return val.charAt(0).toUpperCase() + val.slice(1);
}
function awoTempColors(val) {
  const v = val?.toLowerCase();
  if (v === "warmer") return { bg: "#FAECE7", text: "#993C1D" };
  if (v === "cooler") return { bg: "#E6F1FB", text: "#185FA5" };
  return { bg: "#f1f5f9", text: "#64748b" };
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function Pill({ label, bg, text }) {
  return (
    <Box sx={{ display:"inline-flex", alignItems:"center", px:1.25, py:0.4,
      borderRadius:999, bgcolor:bg, flexShrink:0 }}>
      <Typography sx={{ fontSize:"0.75rem", fontWeight:500, color:text, whiteSpace:"nowrap" }}>
        {label}
      </Typography>
    </Box>
  );
}

function RowItem({ icon, label, pill }) {
  return (
    <Box sx={{ display:"flex", alignItems:"center", justifyContent:"space-between",
      py:1, borderBottom:"0.5px solid", borderColor:"divider", "&:last-child":{borderBottom:"none"} }}>
      <Box sx={{ display:"flex", alignItems:"center", gap:1.25 }}>
        <Typography sx={{ fontSize:"1.1rem", width:22, textAlign:"center", flexShrink:0 }}>{icon}</Typography>
        <Typography sx={{ fontSize:"0.875rem", color:"text.primary" }}>{label}</Typography>
      </Box>
      {pill}
    </Box>
  );
}

function MetricChip({ label, value, sub, dark }) {
  return (
    <Box sx={{ bgcolor:dark?"#0f172a":"#f8fafc", borderRadius:1.5, p:1.25 }}>
      <Typography sx={{ fontSize:"0.7rem", color:"text.secondary", mb:0.25 }}>{label}</Typography>
      <Typography sx={{ fontSize:"1.1rem", fontWeight:500, color:"text.primary", lineHeight:1.2 }}>{value}</Typography>
      {sub && <Typography sx={{ fontSize:"0.7rem", color:"text.secondary" }}>{sub}</Typography>}
    </Box>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function WeatherPage({ colorMode, setColorMode, dark, border, barBg, isMobile }) {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const load = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/pollen");
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (json.error) throw new Error(json.error);
      setData(json);
      setLastRefresh(new Date());
    } catch (e) { setError(e.message); }
    finally { setLoading(false); setRefreshing(false); }
  }, []);

  useEffect(() => {
    load();
    const iv = setInterval(() => load(), 30 * 60 * 1000);
    return () => clearInterval(iv);
  }, [load]);

  const d = data;
  const pollen = d?.pollen;
  const pc = pollenColors(pollen);
  const aq = aqColors(d?.aq);
  const rc = respColors(d?.respiratory_risk);
  const progress = d ? sunProgress(d.sunrise_time, d.sunset_time) : 50;
  const swoActive = parseInt(d?.swo_risk_level) > 0;
  const tickVal = parseInt(d?.bug_ticks) || 0;
  const tickColors = tickVal >= 50 ? { bg:"#FAECE7", text:"#993C1D" } : tickVal >= 20 ? { bg:"#FAEEDA", text:"#854F0B" } : { bg:"#EAF3DE", text:"#3B6D11" };

  return (
    <Box sx={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden" }}>

      {/* Header */}
      <Box sx={{ height:52, display:"flex", alignItems:"center", px:2, gap:1,
        borderBottom:`1px solid ${border}`, bgcolor:barBg, flexShrink:0 }}>
        <WbSunnyIcon sx={{ fontSize:20, color:"#BA7517", flexShrink:0 }} />
        <Typography sx={{ fontSize:"0.85rem", fontWeight:700 }}>Weather & Pollen</Typography>
        <Box sx={{ flex:1 }} />
        {lastRefresh && (
          <Typography variant="caption" sx={{ color:"text.secondary", display:{xs:"none",sm:"block"}, fontSize:"0.72rem" }}>
            {lastRefresh.toLocaleTimeString("en-CA",{hour:"2-digit",minute:"2-digit",hour12:false})}
          </Typography>
        )}
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => load(true)} disabled={refreshing} sx={{ color:"text.secondary" }}>
            {refreshing ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize:20 }} />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Settings">
          <IconButton size="small" onClick={() => setDrawerOpen(true)} sx={{ color:"text.secondary" }}>
            <TuneIcon sx={{ fontSize:20 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Settings drawer */}
      <SwipeableDrawer anchor={isMobile?"bottom":"right"} open={drawerOpen}
        onOpen={() => setDrawerOpen(true)} onClose={() => setDrawerOpen(false)} disableSwipeToOpen
        PaperProps={{ sx:{ bgcolor:barBg, borderTop:isMobile?`1px solid ${border}`:"none",
          borderLeft:!isMobile?`1px solid ${border}`:"none",
          borderRadius:isMobile?"12px 12px 0 0":0, width:isMobile?"100%":280, maxHeight:isMobile?"80vh":"100vh" }}}>
        <Box sx={{ px:2.5, pt:2.5, pb:1.5, display:"flex", alignItems:"center",
          justifyContent:"space-between", borderBottom:`1px solid ${border}` }}>
          <Typography sx={{ fontWeight:700, fontSize:"0.9rem" }}>Settings</Typography>
          <IconButton size="small" onClick={() => setDrawerOpen(false)} sx={{ color:"text.secondary" }}>
            <CloseIcon sx={{ fontSize:20 }} />
          </IconButton>
        </Box>
        <Box sx={{ px:2.5, py:2 }}>
          <Typography sx={{ fontSize:"0.75rem", fontWeight:700, textTransform:"uppercase",
            letterSpacing:"0.07em", color:"text.secondary", mb:1 }}>Appearance</Typography>
          <ToggleButtonGroup value={colorMode} exclusive onChange={(_,v) => v && setColorMode(v)} size="small" fullWidth>
            <ToggleButton value="light" sx={{ flex:1 }}>Light</ToggleButton>
            <ToggleButton value="dark" sx={{ flex:1 }}>Dark</ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </SwipeableDrawer>

      {error && <Alert severity="error" sx={{ mx:2, mt:1, fontSize:"0.82rem" }}>{error}</Alert>}

      {/* Content */}
      <Box sx={{ flex:1, minHeight:0, overflowY:"auto", bgcolor:"background.default" }}>
        {loading ? (
          <Box sx={{ display:"flex", justifyContent:"center", alignItems:"center", height:"100%" }}>
            <CircularProgress size={30} sx={{ color:"#BA7517" }} />
          </Box>
        ) : d && (
          <Box sx={{ px:2, py:2, display:"flex", flexDirection:"column", gap:1.5 }}>

            {/* Current conditions */}
            <Box sx={{ bgcolor:"background.paper", border:`1px solid ${border}`, borderRadius:2, p:2 }}>
              <Box sx={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", mb:2 }}>
                <Box>
                  <Typography sx={{ fontSize:"0.8rem", color:"text.secondary", mb:0.5 }}>
                    Toronto · Main & Danforth
                  </Typography>
                  <Typography sx={{ fontSize:"3.5rem", fontWeight:500, color:"text.primary", lineHeight:1 }}>
                    {d.temp}°
                  </Typography>
                  <Typography sx={{ fontSize:"0.95rem", color:"text.secondary", mt:0.75 }}>
                    {condIcon(d.cond)} {condLabel(d.cond)}
                  </Typography>
                  <Typography sx={{ fontSize:"0.8rem", color:"text.secondary", mt:0.25 }}>
                    Feels like {d.feelslike}°
                  </Typography>
                </Box>
                <Typography sx={{ fontSize:"4rem", lineHeight:1, mt:0.5 }}>
                  {condIcon(d.cond)}
                </Typography>
              </Box>

              <Box sx={{ display:"grid", gridTemplateColumns:"repeat(4, minmax(0,1fr))", gap:1, mb:1.5 }}>
                <MetricChip label="Humidity" value={`${d.humidity}%`} dark={dark} />
                <MetricChip label="Wind" value={d.windspeed} sub="km/h" dark={dark} />
                <MetricChip label="Visibility" value={d.visibility} sub="km" dark={dark} />
                <MetricChip label="Pressure" value={d.pressure} sub="kPa" dark={dark} />
              </Box>

              {/* Sunrise / Sunset */}
              <Box sx={{ borderTop:`1px solid ${border}`, pt:1.5, display:"flex", alignItems:"center", gap:1 }}>
                <Box sx={{ display:"flex", flexDirection:"column", alignItems:"center", gap:0.5, flexShrink:0, minWidth:72 }}>
                  <Typography sx={{ fontSize:"1.5rem" }}>🌅</Typography>
                  <Typography sx={{ fontSize:"0.7rem", color:"text.secondary" }}>Sunrise</Typography>
                  <Typography sx={{ fontSize:"1.1rem", fontWeight:500, color:"text.primary" }}>
                    {fmtTime(d.sunrise_time)}
                  </Typography>
                </Box>
                <Box sx={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:0.75, px:1 }}>
                  <Box sx={{ width:"100%", height:4, bgcolor:dark?"#1e293b":"#f1f5f9",
                    borderRadius:2, position:"relative" }}>
                    <Box sx={{ position:"absolute", left:`${progress}%`, top:"50%",
                      transform:"translate(-50%,-50%)", width:12, height:12,
                      borderRadius:"50%", bgcolor:"#BA7517" }} />
                  </Box>
                  <Typography sx={{ fontSize:"0.7rem", color:"text.secondary" }}>
                    {daylightHours(d.sunrise_time, d.sunset_time)}
                  </Typography>
                </Box>
                <Box sx={{ display:"flex", flexDirection:"column", alignItems:"center", gap:0.5, flexShrink:0, minWidth:72 }}>
                  <Typography sx={{ fontSize:"1.5rem" }}>🌇</Typography>
                  <Typography sx={{ fontSize:"0.7rem", color:"text.secondary" }}>Sunset</Typography>
                  <Typography sx={{ fontSize:"1.1rem", fontWeight:500, color:"text.primary" }}>
                    {fmtTime(d.sunset_time)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Pollen + Air Quality */}
            <Box sx={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:1.5 }}>
              <Box sx={{ bgcolor:"background.paper", border:`1px solid ${border}`, borderRadius:2, p:1.5 }}>
                <Typography sx={{ fontSize:"0.7rem", fontWeight:700, textTransform:"uppercase",
                  letterSpacing:"0.07em", color:"text.secondary", mb:1.25 }}>Pollen</Typography>
                <Box sx={{ display:"flex", alignItems:"center", gap:1.25 }}>
                  <Box sx={{ width:48, height:48, borderRadius:"50%", bgcolor:pc.bg,
                    display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <Typography sx={{ fontSize:"1.4rem" }}>🌿</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize:"1.2rem", fontWeight:500, color:pc.text, lineHeight:1.1 }}>
                      {pollenLabel(pollen)}
                    </Typography>
                    <Typography sx={{ fontSize:"0.7rem", color:"text.secondary", mt:0.25 }}>
                      Aerobiology Research
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <Box sx={{ bgcolor:"background.paper", border:`1px solid ${border}`, borderRadius:2, p:1.5 }}>
                <Typography sx={{ fontSize:"0.7rem", fontWeight:700, textTransform:"uppercase",
                  letterSpacing:"0.07em", color:"text.secondary", mb:1.25 }}>Air quality</Typography>
                <Box sx={{ display:"flex", alignItems:"center", gap:1.25 }}>
                  <Box sx={{ width:48, height:48, borderRadius:"50%", bgcolor:aq.bg,
                    display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <Typography sx={{ fontSize:"1.4rem" }}>💨</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize:"1.2rem", fontWeight:500, color:aq.text, lineHeight:1.1 }}>
                      {aq.label}
                    </Typography>
                    <Typography sx={{ fontSize:"0.7rem", color:"text.secondary", mt:0.25 }}>
                      AQ index
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Health & outdoor indices */}
            <Box sx={{ bgcolor:"background.paper", border:`1px solid ${border}`, borderRadius:2, p:1.5 }}>
              <Typography sx={{ fontSize:"0.7rem", fontWeight:700, textTransform:"uppercase",
                letterSpacing:"0.07em", color:"text.secondary", mb:0.5 }}>Health & outdoor indices</Typography>
              <RowItem icon="🫁" label="Respiratory risk"
                pill={<Pill label={`${d.respiratory_risk} / 10 · ${respLabel(d.respiratory_risk)}`} bg={rc.bg} text={rc.text} />} />
              <RowItem icon="🦴" label="Arthritis"
                pill={<Pill label={levelLabel(d.arthritis)} {...levelColors(d.arthritis)} />} />
              <RowItem icon="🧠" label="Migraine"
                pill={<Pill label={d.migraine === "NO" ? "Low risk" : "Elevated"} {...(d.migraine === "NO" ? {bg:"#EAF3DE",text:"#3B6D11"} : {bg:"#FAECE7",text:"#993C1D"})} />} />
              <RowItem icon="🪲" label="Tick activity"
                pill={<Pill label={`${tickVal} / 100`} bg={tickColors.bg} text={tickColors.text} />} />
              <RowItem icon="🌡️" label="Tomorrow's temperature"
                pill={<Pill label={awoTempLabel(d.awo_temp)} {...awoTempColors(d.awo_temp)} />} />
            </Box>

            {/* Severe weather */}
            <Box sx={{ bgcolor:"background.paper",
              border:`1px solid ${swoActive ? "#ef4444" : border}`,
              borderLeft:`3px solid ${swoActive ? "#ef4444" : (dark?"#1e293b":"#e2e8f0")}`,
              borderRadius:`0 ${16}px ${16}px 0`, p:1.5 }}>
              <Typography sx={{ fontSize:"0.7rem", fontWeight:700, textTransform:"uppercase",
                letterSpacing:"0.07em", color:"text.secondary", mb:1 }}>Severe weather</Typography>
              <Box sx={{ display:"flex", alignItems:"center", gap:1.5 }}>
                <Box sx={{ width:40, height:40, borderRadius:"50%",
                  bgcolor: swoActive ? "#FCEBEB" : "#EAF3DE",
                  display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                  <Typography sx={{ fontSize:"1.2rem" }}>{swoActive ? "⚠️" : "✅"}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize:"0.95rem", fontWeight:500,
                    color: swoActive ? "#A32D2D" : "#3B6D11" }}>
                    {swoActive ? `Warning active · Level ${d.swo_risk_level}` : "No active warnings"}
                  </Typography>
                  <Typography sx={{ fontSize:"0.72rem", color:"text.secondary", mt:0.25 }}>
                    {swoActive ? (d.swo_risk_type || "Severe weather warning") : "SWO risk level: 0"}
                  </Typography>
                </Box>
              </Box>
            </Box>

          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ height:52, bgcolor:barBg, borderTop:`1px solid ${border}`, px:2,
        display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
        <Typography variant="caption" sx={{ color:"text.secondary", fontSize:"0.65rem" }}>
          Pelmorex · Aerobiology Research
        </Typography>
        <Box sx={{ display:"flex", gap:1, alignItems:"center" }}>
          <Box sx={{ width:6, height:6, borderRadius:"50%", bgcolor:"#10b981", animation:"pulse 2s infinite" }} />
          <Typography variant="caption" sx={{ color:"text.secondary", fontSize:"0.65rem" }}>30m</Typography>
        </Box>
      </Box>
    </Box>
  );
}
